// Presentation only: never feed these synthetic rows back into payment, split,
// reserve or stock services. Source ids are not interchangeable after grouping.
const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const minor = value => Math.round(number(value) * 100);
const text = value => String(value ?? '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
const canonical = value => {
  if (typeof value === 'string') {
    try {
      return canonical(JSON.parse(value));
    } catch (_) {
      return text(value);
    }
  }
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  return value ?? null;
};
const extraQuantity = extra => number(extra.quantity ?? extra.selectedQuantity ?? extra.qty ?? 1);
const extraPrice = extra => number(extra.price ?? extra.unit_price ?? extra.unitPrice ?? extra.extra_price);
const unitPrice = item => number(item.unit_price ?? item.unitPrice ?? item.price ?? item.product_price);
// Reserve proofs may contain only opaque extra allocation ids, not a catalogue
// name/price. Keep those allocations distinct rather than guessing equivalence.
const opaqueExtra = extra => extra.name == null && extra.extra_name == null && extra.extraName == null ? canonical(extra) : null;
const extraSignature = (extra, parentQuantity) => JSON.stringify([extra.extra_id ?? extra.extraId ?? null, text(extra.name ?? extra.extra_name ?? extra.extraName), extra.type ?? extra.extra_type ?? null, extraPrice(extra), extra.quantity / parentQuantity, minor(extra.total_price) / parentQuantity, canonical(extra.variation_attributes), opaqueExtra(extra)]);
const defaultTotal = item => {
  const explicit = item.total_price_with_extras ?? item.totalPriceWithExtras;
  if (explicit != null && Number.isFinite(Number(explicit))) return Number(explicit);
  const qty = number(item.quantity ?? item.qty ?? 1);
  const base = item.total_price ?? item.totalPrice ?? item.total;
  const extras = number(item.extras_total ?? item.extrasTotal ?? (item.extras || []).reduce((sum, extra) => sum + (extra.type === 'accompaniment' ? 0 : number(extra.total_price ?? extra.totalPrice ?? extraPrice(extra) * extraQuantity(extra))), 0));
  return base != null ? number(base) + extras : (unitPrice(item) + extras) * qty;
};

// Persisted lines carry absolute Extras; draft lines carry per-unit Extras.
// The normalized final amount is always authoritative, including explicit zero.
// Different catalogue/variant/price/option profiles deliberately stay separate.
function groupPrintedItemGroups(items, {
  getLineTotal = defaultTotal,
  extrasMode = 'auto'
} = {}) {
  const groups = new Map();
  const output = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!item || typeof item !== 'object') continue;
    const qty = number(item.quantity ?? item.qty ?? 1);
    const total = getLineTotal(item);
    if (!(qty > 0) || !Number.isFinite(total)) {
      output.push({ item: {
        ...item,
        extras: (item.extras || []).map(extra => ({
          ...extra
        }))
      }, members: [item] });
      continue;
    }
    const mode = item.__printedExtrasMode || (extrasMode === 'auto' ? item.total_price != null || item.totalPrice != null || item.total_price_with_extras != null ? 'line' : 'unit' : extrasMode);
    const extras = (Array.isArray(item.extras) ? item.extras : []).map(extra => {
      const quantity = extraQuantity(extra) * (mode === 'unit' ? qty : 1);
      const explicit = extra.total_price ?? extra.totalPrice;
      const value = extra.type === 'accompaniment' ? 0 : explicit != null ? number(explicit) * (mode === 'unit' ? qty : 1) : extraPrice(extra) * quantity;
      return {
        ...extra,
        quantity,
        selectedQuantity: quantity,
        qty: quantity,
        total_price: number(value),
        totalPrice: number(value)
      };
    });
    const extraKey = extra => extraSignature(extra, qty);
    const signature = JSON.stringify([item.product_id ?? item.productId ?? null, text(item.display_name ?? item.displayName ?? item.name ?? item.product_name ?? item.label), item.variation_id ?? item.variationId ?? null, text(item.variation_name ?? item.variation_label ?? item.variationName ?? item.variationLabel), text(item.variationLabel), canonical(item.variation_attributes ?? item.variationAttributes ?? item.variation_attributes_ref ?? item.attributes), unitPrice(item), minor(total) / qty, minor(item.discount_amount ?? item.item_discount) / qty, item.discount_percent ?? item.discount_percentage ?? null, text(item.notes ?? item.special_instructions ?? item.comment), Boolean(item.is_complimentary ?? item.is_free), extras.map(extraKey).sort()]);
    const existing = groups.get(signature);
    const base = number(item.total_price ?? item.totalPrice ?? item.total ?? unitPrice(item) * qty);
    if (!existing) {
      const row = {
        ...item,
        quantity: qty,
        qty,
        total_price: minor(base) / 100,
        totalPrice: minor(base) / 100,
        total_price_with_extras: minor(total) / 100,
        totalPriceWithExtras: minor(total) / 100,
        extras: extras.map(extra => ({
          ...extra
        })),
        __printedExtrasMode: 'line'
      };
      const entry = { item: row, members: [item] };
      groups.set(signature, {
        row,
        entry,
        total: minor(total),
        base: minor(base),
        discount: minor(item.discount_amount ?? item.item_discount)
      });
      output.push(entry);
    } else {
      const row = existing.row;
      existing.entry.members.push(item);
      row.quantity += qty;
      row.qty = row.quantity;
      existing.total += minor(total);
      existing.base += minor(base);
      existing.discount += minor(item.discount_amount ?? item.item_discount);
      row.total_price = row.totalPrice = existing.base / 100;
      row.total_price_with_extras = row.totalPriceWithExtras = existing.total / 100;
      if (row.discount_amount != null) row.discount_amount = existing.discount / 100;
      if (row.item_discount != null) row.item_discount = existing.discount / 100;
      const incoming = new Map();
      for (const extra of extras) {
        const key = extraKey(extra);
        if (!incoming.has(key)) incoming.set(key, []);
        incoming.get(key).push(extra);
      }
      for (const extra of row.extras) {
        // Match the per-unit profile using the previous parent quantity.
        const oldQty = row.quantity - qty;
        const key = extraSignature(extra, oldQty);
        const next = incoming.get(key)?.shift();
        if (next) {
          extra.quantity += next.quantity;
          extra.selectedQuantity = extra.qty = extra.quantity;
          extra.total_price = (minor(extra.total_price) + minor(next.total_price)) / 100;
          extra.totalPrice = extra.total_price;
        }
      }
    }
  }
  return output;
}
function groupPrintedItems(items, options) {
  return groupPrintedItemGroups(items, options).map(group => group.item);
}
function groupPrintedReserveLines(lines) {
  return groupPrintedItems((lines || []).map(line => ({
    ...line,
    name: line.label,
    total_price_with_extras: number(line.netMinor) / 100
  }))).map(line => ({
    ...line,
    netMinor: minor(line.total_price_with_extras)
  }));
}
module.exports = {
  groupPrintedItems,
  groupPrintedItemGroups,
  groupPrintedReserveLines,
  printedLineTotal: defaultTotal
};
